import React, { useState, useEffect, useCallback } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import useAPI from '../../../api';
import {
  openSnackbarAdviseAtom,
  endedCallDataAtom,
  additionalCustomerDataAtom,
  selectedBranchAtom,
  userDataAtom,
  reloadAtom,
  activityTypesAtom,
} from '../../../Atoms';
import {
  FullSizeCenteredColumn,
  FullSizeColumn,
} from '../../../Components/FlexBox';
import WidgetBox from '../../../Components/Paper/WidgetBox';
import CallResultBox from './ResultBox';
import CallBox from './CallBox';
import ContentError from '../../../Components/Error/ContentError';
import {
  notifyEndCallAtom,
  notifyUpdateCallAtom,
  notifyIncomingCallAtom,
} from '../../../Atoms/HubAtoms';
import useLocationName from '../../../Hooks/useLocationName';
import { createNewDate } from '../../../Helpers/dates';
import { getCallAnsweredState } from '../../../Helpers/calls';
import { APPOINTMENT_TIME } from '../../AppointmentsPage/Utils/constants';

const numberRegex = /^\+?\d+$/; //accept only numbers and the + at start

const CallSection = function CallSection({
  reloadHistory,
  campaignId,
  contactId,
  contactIndex,
  userRoles,
  isLead,
  activityParentId,
  selectedCustomer,
  selectedPhoneNumber,
  handleChangeSelectedPhoneNumber,
  changePhoneOrMail,
}) {
  const intl = useIntl();
  const API = useAPI();
  const locationName = useLocationName();

  const activityTypes = useAtomValue(activityTypesAtom);
  const userData = useAtomValue(userDataAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const setEndedCallData = useSetAtom(endedCallDataAtom);
  const additionalCustomerData = useAtomValue(additionalCustomerDataAtom);
  const notifyEndCall = useAtomValue(notifyEndCallAtom);
  const notifyUpdateCall = useAtomValue(notifyUpdateCallAtom);
  const notifyIncomingCall = useAtomValue(notifyIncomingCallAtom);
  const setReload = useSetAtom(reloadAtom);

  const [phoneTypes, setPhoneTypes] = useState([]);
  const [contactPhones, setContactPhones] = useState([]);
  const [callInfos, setCallInfos] = useState(null);
  const [appointmentToManage, setAppointmentToManage] = useState(null);

  /**How answered works: (boolean | null)
   * When a call is started the answered is null, until the user clicks "Answered" or "Not answered" or the device
   * integration sets automatically to one of them. "Answered" will set the state to true, "Not answered" to false
   * to filter the outcomes.
   * When the call is completed with outcome is setted to null.
   */
  const [answered, setAnswered] = useState(null);
  const [callBoxState, setCallBoxState] = useState(null);
  const [resultBoxState, setResultBoxState] = useState(false);

  const handleChangeAnswered = (newValue) => {
    setAnswered(newValue === answered ? null : newValue);
  };

  //restore the no-call ui (no outcome dialog, ready to dial)
  const resetCallUI = () => {
    setResultBoxState(false);
    setCallBoxState(null);
    setAnswered(null);
    setCallInfos(null);
  };

  //reflect an inbound call in the ui (open the call box in its current state)
  const applyIncomingCall = (call) => {
    const answeredState = getCallAnsweredState(call);
    //inbound unanswered call: skip the outcome dialog, keep the no-call ui
    if (call.outbound === false && answeredState === false) {
      resetCallUI();
      return;
    }
    setCallInfos(call);
    setCallBoxState(true);
    setResultBoxState(true);
    setAnswered(answeredState);
  };

  useEffect(() => {
    //when reaching the page from the incoming call popup
    if (
      additionalCustomerData?.callData &&
      additionalCustomerData?.callData.id
    ) {
      applyIncomingCall(additionalCustomerData.callData);
    }
  }, [additionalCustomerData]);

  useEffect(() => {
    //incoming call for the contact already open: reflect it without clicking the popup
    if (
      notifyIncomingCall?.id &&
      contactId &&
      notifyIncomingCall.entityId === contactId
    ) {
      applyIncomingCall(notifyIncomingCall);
    }
  }, [notifyIncomingCall]);

  useEffect(() => {
    if (
      selectedCustomer?.lastCallStatus &&
      selectedCustomer.lastCallOutcome === 0 &&
      !(additionalCustomerData?.callData && additionalCustomerData?.callData.id)
    ) {
      setCallBoxState(true);
      setResultBoxState(true);
    }
  }, [contactId]);

  useEffect(() => {
    if (selectedCustomer && selectedCustomer.contacts.length > 0) {
      setContactPhones(selectedCustomer?.contacts[contactIndex]?.contactPhones);
    }
  }, [selectedCustomer, contactIndex]);

  useEffect(() => {
    API.getPhoneTypes(
      (res) => {
        const phoneTypesOptions = res.map((item) => ({
          label: item.phoneType,
          value: item.phoneNumberTypeId,
          isPriority: item.isPriority,
        }));
        setPhoneTypes(phoneTypesOptions);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.PHONENUMBER_TYPE_LOADING_ERROR,
          severity: 'error',
        });
      }
    );
  }, []);

  //recompute the selected number from a phone list: active priority first, then any active, else none
  const refreshSelectedPhoneNumber = (phones) => {
    const activePhones = phones?.filter(({ active }) => active) || [];
    handleChangeSelectedPhoneNumber(
      (activePhones.find(({ priority }) => priority) || activePhones[0])
        ?.phoneNumber || null
    );
  };

  const handleChangeSelectedPhone = (phoneNumber) => {
    API.setPhoneAsDefault(
      contactId,
      phoneNumber,
      1, //Priority,
      () => {
        handleChangeSelectedPhoneNumber(phoneNumber);
      },
      () => {}
    );
  };

  const handleChangePhoneNumberStatus = (phone, isPriority, isMobile) => {
    API.changePhoneNumberStatus(
      contactId,
      phone,
      () => {
        const copyArray = [...contactPhones];
        let item = copyArray.find(({ phoneNumber }) => phoneNumber === phone);
        const index = copyArray.findIndex(
          ({ phoneNumber }) => phoneNumber === phone
        );

        item = Object.assign(item, { active: !item.active });
        copyArray.splice(index, 1, item);

        setContactPhones(copyArray);
        //activate/deactivate may change which number should be selected
        refreshSelectedPhoneNumber(copyArray);
        if (isPriority) {
          changePhoneOrMail(phone, true, isMobile, false);
        }
      },
      () => {}
    );
  };

  const handleAddNewPhoneNumber = (
    newPhone,
    newPhoneType,
    priority,
    isPrincipal,
    isMobile
  ) => {
    const payloadPhoneNumber =
      newPhone.substring(0, 3) === '+39' ? newPhone : `+39${newPhone}`;
    if (payloadPhoneNumber.match(numberRegex)) {
      API.addNewPhoneNumber(
        contactId,
        payloadPhoneNumber, //number
        newPhoneType.value, //phoneType
        priority,
        () => {
          changePhoneOrMail(
            {
              phoneNumber: payloadPhoneNumber,
              phoneTypeId: newPhoneType.value,
              phoneType: newPhoneType.label,
              priority,
              isPrincipal,
            },
            true,
            isMobile,
            true
          );
          //a new active number may become the one that should be selected
          refreshSelectedPhoneNumber([
            ...contactPhones,
            { phoneNumber: payloadPhoneNumber, priority, active: true },
          ]);
        },
        () => {
          setOpenSnackbarAdvise({
            message: intl.messages.ERROR_ADD_PHONENUMBER,
            severity: 'error',
          });
        }
      );
    }
  };

  const handleStartCall = useCallback(() => {
    API.activity(
      {
        entityId: contactId,
        branchId: selectedBranch.id,
        activityTypeId: 3,
        campaignId,
      },
      {
        phoneNumber: selectedPhoneNumber,
        sectionId: additionalCustomerData?.sectionId ?? locationName,
        wavePeriod: additionalCustomerData?.wavePeriod,
        outbound: true,
      },
      {},
      (result) => {
        setAnswered(null);
        setCallBoxState(true);
        setResultBoxState(true);
        setCallInfos(result);
        setOpenSnackbarAdvise({
          message: intl.messages.CALL_STARTED,
          severity: 'info',
        });
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.CALLSTART_ERROR,
          severity: 'error',
        });
      }
    );
  }, [contactId, selectedPhoneNumber, additionalCustomerData, locationName]);

  const handleStartRecall = useCallback(() => {
    API.activity(
      {
        entityId: contactId,
        branchId: selectedBranch.id,
        activityTypeId: 3,
        activityId: activityParentId,
        campaignId,
      },
      {
        phoneNumber: selectedPhoneNumber,
        sectionId: additionalCustomerData?.sectionId ?? locationName,
        wavePeriod: additionalCustomerData?.wavePeriod,
        outbound: true,
      },
      {},
      (result) => {
        setReload(true); //reloads the appointments and recall in calendar page
        setAnswered(null);
        setCallBoxState(true);
        setResultBoxState(true);
        setOpenSnackbarAdvise({
          message: intl.messages.CALL_STARTED,
          severity: 'info',
        });
        setCallInfos(result);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.CALLSTART_ERROR,
          severity: 'error',
        });
      }
    );
  }, [contactId, selectedPhoneNumber, additionalCustomerData, locationName]);

  const handleEndCall = (callResult) => {
    const payload = {
      ...selectedCustomer,
      callOutcome: {
        ...callResult,
        callId: callInfos?.id || selectedCustomer.lastCallId,
      },
    };
    API.endCall(
      payload?.callOutcome?.callId,
      payload,
      additionalCustomerData?.activityTypeId ? false : true, //if call is started from PRIORITY section no sms after call will be sent
      (result) => {
        setOpenSnackbarAdvise({
          message: intl.messages.CALL_ENDED,
          severity: 'info',
        });
        setEndedCallData(result);
        setResultBoxState(false);
        if (callBoxState === false) setCallBoxState(null);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.CALLEND_ERROR,
          severity: 'error',
        });
      }
    );
  };
  /******/

  useEffect(() => {
    //follow the live call status: connected -> answered, disconnected (never connected) -> not answered
    if (
      notifyUpdateCall &&
      callInfos &&
      notifyUpdateCall?.id === callInfos?.id
    ) {
      if (notifyUpdateCall.connectedOn) {
        setAnswered(true);
      } else if (notifyUpdateCall.disconnectedOn) {
        //inbound unanswered call: skip the outcome dialog and reset to the no-call state
        if (callInfos?.outbound === false) {
          resetCallUI();
        } else {
          setAnswered(false);
        }
      }
    }
  }, [notifyUpdateCall]);

  useEffect(() => {
    if (notifyEndCall && callInfos && notifyEndCall?.id === callInfos?.id) {
      if (notifyEndCall.outcome) {
        setResultBoxState(false);
        if (!userData?.voIPDevice) {
          setAnswered(null);
          setCallBoxState(null);
        }
        if (notifyEndCall.callStatus === 'Disconnected') {
          setCallBoxState(null);
        }
        return;
      }
      if (notifyEndCall.callStatus === 'Disconnected') {
        //inbound unanswered call: skip the outcome dialog and reset to the no-call state
        const callWasAnswered =
          answered === true || getCallAnsweredState(notifyEndCall) === true;
        if (callInfos?.outbound === false && !callWasAnswered) {
          resetCallUI();
          return;
        }
        setCallBoxState(resultBoxState ? false : null);
        setAnswered(notifyEndCall?.answered);
      } else {
        setCallBoxState(null);
      }
    }
  }, [notifyEndCall]);

  useEffect(() => {
    if (!additionalCustomerData?.activityTypeId) return;

    if (
      activityTypes?.find(
        ({ id }) => id === additionalCustomerData.activityTypeId
      )?.name === 'APPOINTMENT'
    ) {
      API.getActivity(
        additionalCustomerData?.parentId,
        ({
          id,
          entityId,
          scheduledOn,
          entityFullName,
          baseEntityFullName,
          createdByFullName,
          notes,
          outcome,
          commercialCampaignId,
          assignee,
          remindBefore,
        }) => {
          const date = createNewDate(scheduledOn);
          setAppointmentToManage({
            entityId: entityId,
            contactFullName: entityFullName,
            customerFullName: baseEntityFullName,
            createdByFullName,
            commercialCampaignId: commercialCampaignId ?? null,
            appointmentId: id,
            title: `${baseEntityFullName} ${baseEntityFullName !== entityFullName ? `${entityFullName && !baseEntityFullName ? `${entityFullName ?? ''}` : `- ${entityFullName ?? ''}`}` : ''}`,
            type: 'Appuntamento',
            desc: notes,
            startDate: date,
            endDate: new Date(date.getTime() + APPOINTMENT_TIME),
            outcome,
            assignee,
            remindBefore,
          });
        },
        () => {}
      );
    }
  }, [additionalCustomerData, activityTypes]);

  const isOperator = Boolean(
    userRoles?.find(
      ({ roleId }) => roleId === 'OPERATOR' || roleId === 'LIMITEDOPERATOR'
    )
  );
  return (
    <>
      <FullSizeColumn flex="0 0 30%" gap="1.5vh">
        <CallBox
          contactExpired={additionalCustomerData?.remainingDays === 0}
          //inbound calls are driven by the hub update; outbound still relies on the voIP device
          disableAnsweredButtons={
            callInfos?.outbound === false || userData?.voIPDevice
          }
          campaignId={campaignId}
          reloadHistory={reloadHistory}
          contactId={contactId}
          isOperator={isOperator}
          changePhoneOrMail={changePhoneOrMail}
          isLead={isLead}
          callId={callInfos?.id}
          callBoxState={callBoxState}
          resultBoxState={resultBoxState}
          activityParentId={activityParentId}
          answered={answered}
          handleChangeAnswered={handleChangeAnswered}
          lastContactDate={selectedCustomer.lastCallCompletedOn}
          contactPhones={contactPhones} //array of numbers
          selectedPhoneNumber={selectedPhoneNumber}
          handleChangeSelectedPhone={handleChangeSelectedPhone}
          handleAddNewPhoneNumber={handleAddNewPhoneNumber}
          handleChangeContactStatus={handleChangePhoneNumberStatus}
          handleStartCall={handleStartCall}
          handleStartRecall={handleStartRecall}
          phoneTypes={phoneTypes}
          contactName={selectedCustomer?.contacts[contactIndex]?.fullName}
          removedOn={selectedCustomer?.removedOn}
          appointmentToManage={appointmentToManage}
        />
        <WidgetBox
          flex="1 1 55%"
          title={intl.messages.CALL_OUTCOME}
          widget={
            !resultBoxState ? (
              <FullSizeCenteredColumn>
                <ContentError
                  text={intl.messages.START_A_CALL}
                  icon={
                    <CallRoundedIcon
                      sx={{ color: '#acacac', fontSize: '56px' }}
                    />
                  }
                />
              </FullSizeCenteredColumn>
            ) : (
              <CallResultBox
                campaignId={campaignId}
                phoneNumber={selectedPhoneNumber}
                sectionId={additionalCustomerData?.sectionId ?? locationName}
                wavePeriod={additionalCustomerData?.wavePeriod}
                answered={answered}
                handleEndCall={handleEndCall}
                isOperator={isOperator}
              />
            )
          }
        />
      </FullSizeColumn>
    </>
  );
};

CallSection.propTypes = {
  contactId: PropTypes.string,
  activityParentId: PropTypes.string,
  selectedCustomer: PropTypes.object,
  selectedPhoneNumber: PropTypes.string,
  handleChangeSelectedPhoneNumber: PropTypes.func,
  changePhoneOrMail: PropTypes.func,
};
export default CallSection;
